/**
 * S3 Custom Font CDN Service
 * Simple service for uploading fonts to S3 and using them as a CDN
 */

import { getSignedUrl } from "../../../../graphQL/query/file";
import { authorization } from "../../../../module/auth";
import { saveUserFonts, getUserFonts, removeUserFont, UserFontData } from "../../../../graphQL/mutations/userFonts";

export interface FontCDNData {
  name: string;
  s3Url: string;
  mimeType: string;
  uploadedAt: number;
  fileSize: number;
  weight?: string; // Detected weight from font file metadata
}

class S3CustomFontService {
  private static readonly FONT_KEY_PREFIX = "campaign-builder/font-cdn/";
  // In-memory cache to avoid reloading fonts repeatedly in the same session
  private cache: {
    userId: string;
    clientId: number;
    fonts: FontCDNData[];
  } | null = null;

  /**
   * Upload a custom font file to S3 as CDN resource
   */
  async uploadFontToCDN(file: File, fontName: string): Promise<FontCDNData> {
    try {
      // Generate unique key for the font file
      const timestamp = Date.now();
      const sanitizedFontName = encodeURIComponent(fontName).replace(/[^a-zA-Z0-9]/g, "");
      const fileExtension = file.name.split(".").pop()?.toLowerCase() || "ttf";

      const s3Key = `${S3CustomFontService.FONT_KEY_PREFIX}${sanitizedFontName}-${timestamp}.${fileExtension}`;

      console.log("🔄 Uploading font to S3", { fontName, s3Key });

      // Get signed URL for upload with public-read access
      const signedUrlResponse = await getSignedUrl("oppizi-uploads", s3Key, "PUT", "ap-southeast-2", "public-read");

      console.log("📝 Raw signed URL response:", signedUrlResponse);

      // Handle potential error response from getSignedUrl
      if (signedUrlResponse instanceof Error) {
        throw new Error(`Failed to get signed URL: ${signedUrlResponse.message}`);
      }

      // Extract the actual URL string
      let signedUrlPUT: string;
      if (typeof signedUrlResponse === "string") {
        signedUrlPUT = signedUrlResponse;
      } else if (signedUrlResponse && typeof signedUrlResponse === "object" && signedUrlResponse.url) {
        signedUrlPUT = signedUrlResponse.url;
      } else if (signedUrlResponse && typeof signedUrlResponse === "object" && signedUrlResponse.data?.getSignedUrl) {
        signedUrlPUT = signedUrlResponse.data.getSignedUrl;
      } else {
        console.error("Invalid signed URL response structure:", signedUrlResponse);
        throw new Error(`Invalid signed URL response. Expected string, got: ${typeof signedUrlResponse}`);
      }

      console.log("✅ Extracted signed URL:", signedUrlPUT);

      // Validate that we got a proper signed URL
      if (!signedUrlPUT || typeof signedUrlPUT !== "string" || !signedUrlPUT.startsWith("http")) {
        console.error("Invalid signed URL:", signedUrlPUT);
        throw new Error(`Invalid signed URL format: ${signedUrlPUT}`);
      }

      // Upload to S3 using signed URL
      const uploadResponse = await fetch(signedUrlPUT, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type || "font/ttf",
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      // Get public URL for the font (no signed URL needed for public-read)
      const publicUrl = `https://oppizi-uploads.s3.ap-southeast-2.amazonaws.com/${s3Key}`;

      // Create font CDN data
      const fontData: FontCDNData = {
        name: fontName,
        s3Url: publicUrl,
        mimeType: file.type || "font/ttf",
        uploadedAt: timestamp,
        fileSize: file.size,
      };

      // Store font info locally for UI
      await this.addFontToLocalRegistry(fontData);

      console.log("✅ Font uploaded to S3 CDN", { fontName, publicUrl });
      return fontData;
    } catch (error) {
      console.error("Error uploading font to S3 CDN:", error);

      // Development fallback: if S3 upload fails, store locally and show warning
      if (process.env.NODE_ENV === "development" || window.location.hostname === "localhost") {
        console.warn("⚠️ S3 upload failed in development, falling back to local storage for font", { fontName });

        try {
          // Create a data URL for the font
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error("Failed to read file"));
            reader.readAsDataURL(file);
          });

          // Create fallback font data with data URL
          const fallbackFontData: FontCDNData = {
            name: fontName,
            s3Url: dataUrl, // Use data URL as fallback
            mimeType: file.type || "font/ttf",
            uploadedAt: Date.now(),
            fileSize: file.size,
          };

          // Store in local registry
          await this.addFontToLocalRegistry(fallbackFontData);

          console.log("✅ Font stored locally as development fallback", { fontName });
          return fallbackFontData;
        } catch (fallbackError) {
          console.error("Fallback storage also failed:", fallbackError);
          throw new Error(`Both S3 and fallback storage failed: ${error}`);
        }
      } else {
        throw new Error(`Failed to upload font to S3 CDN: ${error}`);
      }
    }
  }

  /**
   * Convert FontCDNData (number) to UserFontData (string) for GraphQL
   */
  private convertToUserFontData(font: FontCDNData): UserFontData {
    return {
      name: font.name,
      s3Url: font.s3Url,
      mimeType: font.mimeType,
      uploadedAt: String(font.uploadedAt),
      fileSize: String(font.fileSize),
    };
  }

  /**
   * Convert UserFontData (string) to FontCDNData (number) from GraphQL
   */
  private convertFromUserFontData(font: UserFontData): FontCDNData {
    return {
      name: font.name,
      s3Url: font.s3Url,
      mimeType: font.mimeType,
      uploadedAt: parseInt(font.uploadedAt, 10),
      fileSize: parseInt(font.fileSize, 10),
    };
  }

  /**
   * Load fonts from backend
   */
  private async loadFontsFromBackend(): Promise<FontCDNData[]> {
    try {
      const user = authorization.getUser();
      if (!user || !user.id) {
        console.warn("No user logged in, skipping backend load");
        return [];
      }

      const response = (await getUserFonts()) as any;
      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0]?.message || "Failed to load fonts from backend");
      }

      // Convert from UserFontData (strings) to FontCDNData (numbers)
      const backendFontsRaw: UserFontData[] = result.data?.getUserFonts || [];
      const backendFonts: FontCDNData[] = backendFontsRaw.map((font) => this.convertFromUserFontData(font));

      console.log("✅ Loaded fonts from backend", { count: backendFonts.length });
      return backendFonts;
    } catch (error) {
      console.error("Error loading fonts from backend:", error);
      return [];
    }
  }

  /**
   * Sync fonts with backend
   */
  private async syncFontsWithBackend(fonts: FontCDNData[]): Promise<void> {
    try {
      const user = authorization.getUser();
      if (!user || !user.id) {
        console.warn("No user logged in, skipping backend sync");
        return;
      }

      const userFontData: UserFontData[] = fonts.map((font) => this.convertToUserFontData(font));
      const response = (await saveUserFonts(userFontData)) as any;
      const result = await response.json();

      if (result.errors) {
        console.error("❌ Backend error saving fonts:", result.errors);
        throw new Error(`Backend error: ${result.errors[0]?.message || JSON.stringify(result.errors)}`);
      }
    } catch (error) {
      console.error("❌ Error syncing fonts with backend:", error);
      // Re-throw to allow caller to handle
      throw error;
    }
  }

  /**
   * Add font to local registry for UI management
   */
  private async addFontToLocalRegistry(fontData: FontCDNData): Promise<void> {
    try {
      const user = authorization.getUser();
      const currentUserId = user?.id;
      const currentClientId = user?.clientId;

      if (!currentUserId || !currentClientId) {
        console.warn("⚠️ No user logged in or missing userId/clientId, cannot add font to registry");
        return;
      }
      const existingFonts = await this.getLocalFontRegistry();

      // Remove any existing font with the same name
      const updatedFonts = existingFonts.filter((font) => font.name !== fontData.name);
      updatedFonts.push(fontData);

      // Store updated list locally with userId and clientId
      const storageData = {
        userId: currentUserId,
        clientId: currentClientId,
        fonts: updatedFonts,
      };
      localStorage.setItem("s3_font_cdn_registry", JSON.stringify(storageData));
      // Sync only the new font with backend (not all fonts to avoid duplicates)
      try {
        // Check if this font already exists in backend by comparing with existing fonts
        const backendFonts = await this.loadFontsFromBackend();
        const fontExistsInBackend = backendFonts.some((font) => font.name === fontData.name);

        if (fontExistsInBackend) {
          console.log("ℹ️ Font already exists in backend, skipping sync", { fontName: fontData.name });
        } else {
          // Only sync the new font, not all fonts
          await this.syncFontsWithBackend([fontData]);
        }
      } catch (syncError) {
        console.error("❌ Failed to sync font to backend", { fontName: fontData.name, syncError });
        // Don't throw here - font is still stored locally, sync can be retried later
      }
    } catch (error) {
      console.error("❌ Error adding font to local registry:", error);
      throw error;
    }
  }

  /**
   * Get all fonts from local registry (with backend sync)
   */
  async getLocalFontRegistry(): Promise<FontCDNData[]> {
    try {
      const user = authorization.getUser();
      const currentUserId = user?.id;
      const currentClientId = user?.clientId;

      // If no user is logged in, don't return any fonts
      if (!currentUserId || !currentClientId) {
        console.warn("No user logged in or missing userId/clientId, clearing fonts");
        localStorage.removeItem("s3_font_cdn_registry");
        return [];
      }

      // In-memory cache (session)
      if (
        this.cache &&
        this.cache.userId === currentUserId &&
        this.cache.clientId === currentClientId &&
        Array.isArray(this.cache.fonts)
      ) {
        return this.cache.fonts;
      }

      // Try to load from backend first (for persistence across sessions)
      const backendFonts = await this.loadFontsFromBackend();

      if (backendFonts.length > 0) {
        // Update localStorage with backend data, including userId and clientId
        const storageData = {
          userId: currentUserId,
          clientId: currentClientId,
          fonts: backendFonts,
        };
        localStorage.setItem("s3_font_cdn_registry", JSON.stringify(storageData));
        // Update cache
        this.cache = { userId: currentUserId, clientId: currentClientId, fonts: backendFonts };
        return backendFonts;
      }

      // Fallback to localStorage if backend has no fonts
      const stored = localStorage.getItem("s3_font_cdn_registry");

      if (!stored) {
        return [];
      }

      // Parse stored data - check if it's the new format (with userId/clientId) or old format
      let storedData: { userId?: string; clientId?: number; fonts?: FontCDNData[] } | FontCDNData[];
      try {
        storedData = JSON.parse(stored);
      } catch {
        // Invalid JSON, clear it
        localStorage.removeItem("s3_font_cdn_registry");
        return [];
      }

      // Handle old format (array of fonts directly) - REJECT IT if user is logged in
      let localFontsRaw: FontCDNData[];
      let storedUserId: string | undefined;
      let storedClientId: number | undefined;

      if (Array.isArray(storedData)) {
        // Old format - no userId/clientId stored
        // Since we have a logged-in user, we should reject old format fonts
        // They could belong to any user
        console.warn("Found old format fonts in localStorage (no userId/clientId), clearing them for security");
        localStorage.removeItem("s3_font_cdn_registry");
        return [];
      } else {
        // New format - check userId and clientId
        storedUserId = storedData.userId;
        storedClientId = storedData.clientId;
        localFontsRaw = storedData.fonts || [];

        // STRICT validation: both userId and clientId must match
        // If either is missing or doesn't match, reject the fonts
        if (!storedUserId || !storedClientId) {
          console.warn("Fonts in localStorage missing userId or clientId, clearing them", {
            storedUserId,
            storedClientId,
            currentUserId,
            currentClientId,
          });
          localStorage.removeItem("s3_font_cdn_registry");
          return [];
        }

        // Both must match exactly
        if (storedUserId !== currentUserId || storedClientId !== currentClientId) {
          console.warn("Fonts in localStorage belong to a different user/client, clearing them", {
            storedUserId,
            currentUserId,
            storedClientId,
            currentClientId,
          });
          localStorage.removeItem("s3_font_cdn_registry");
          return [];
        }
      }

      // Ensure proper typing - convert to FontCDNData format
      const localFonts: FontCDNData[] = localFontsRaw.map((font: any) => ({
        name: font.name,
        s3Url: font.s3Url,
        mimeType: font.mimeType,
        uploadedAt: typeof font.uploadedAt === "number" ? font.uploadedAt : parseInt(String(font.uploadedAt), 10),
        fileSize: typeof font.fileSize === "number" ? font.fileSize : parseInt(String(font.fileSize), 10),
      }));

      // Only sync local fonts if backend is empty AND userId/clientId matches
      // Don't sync on every call to avoid sending duplicates
      if (localFonts.length > 0 && backendFonts.length === 0) {
        // Only sync if userId and clientId match (they should match at this point)
        const shouldSync = storedUserId === currentUserId && storedClientId === currentClientId;

        if (shouldSync) {
          // Only sync once, not every time
          const lastSyncKey = `s3_font_cdn_last_sync_${currentUserId}_${currentClientId}`;
          const lastSync = localStorage.getItem(lastSyncKey);
          const now = Date.now();

          // Only sync if we haven't synced in the last 5 minutes
          if (!lastSync || now - parseInt(lastSync, 10) > 5 * 60 * 1000) {
            await this.syncFontsWithBackend(localFonts);
            localStorage.setItem(lastSyncKey, String(now));
          }
        }
      }

      // Update cache with validated local fonts
      this.cache = { userId: currentUserId, clientId: currentClientId, fonts: localFonts };
      return localFonts;
    } catch (error) {
      console.error("Error getting local font registry:", error);
      // Fallback to localStorage only - but with strict validation
      try {
        const user = authorization.getUser();
        const currentUserId = user?.id;
        const currentClientId = user?.clientId;

        // If no user is logged in, don't return any fonts
        if (!currentUserId || !currentClientId) {
          return [];
        }

        const stored = localStorage.getItem("s3_font_cdn_registry");
        if (!stored) return [];

        const storedData = JSON.parse(stored);

        // Reject old format (Array)
        if (Array.isArray(storedData)) {
          console.warn("Found old format fonts in fallback, clearing them");
          localStorage.removeItem("s3_font_cdn_registry");
          return [];
        }

        const localFontsRaw = storedData.fonts || [];
        const storedUserId = storedData.userId;
        const storedClientId = storedData.clientId;

        // STRICT validation: both userId and clientId must exist and match
        if (!storedUserId || !storedClientId) {
          console.warn("Fonts missing userId or clientId in fallback, clearing them");
          localStorage.removeItem("s3_font_cdn_registry");
          return [];
        }

        if (storedUserId !== currentUserId || storedClientId !== currentClientId) {
          console.warn("Fonts belong to different user/client in fallback, clearing them");
          localStorage.removeItem("s3_font_cdn_registry");
          return [];
        }

        // Ensure proper typing
        const fallbackFonts = localFontsRaw.map((font: any) => ({
          name: font.name,
          s3Url: font.s3Url,
          mimeType: font.mimeType,
          uploadedAt: typeof font.uploadedAt === "number" ? font.uploadedAt : parseInt(String(font.uploadedAt), 10),
          fileSize: typeof font.fileSize === "number" ? font.fileSize : parseInt(String(font.fileSize), 10),
        }));
        this.cache = { userId: currentUserId, clientId: currentClientId, fonts: fallbackFonts };
        return fallbackFonts;
      } catch {
        // If anything fails, clear localStorage and return empty
        localStorage.removeItem("s3_font_cdn_registry");
        return [];
      }
    }
  }

  /**
   * Load custom fonts from S3 CDN URLs and add them to document.fonts
   * Loads all fonts in parallel for better performance
   */
  async loadFontsIntoDocument(): Promise<string[]> {
    const loadedFontNames: string[] = [];

    try {
      const cdnFonts = await this.getLocalFontRegistry();

      if (cdnFonts.length === 0) {
        return loadedFontNames;
      }

      // Load all fonts in parallel instead of sequentially
      const fontLoadPromises = cdnFonts.map(async (fontData) => {
        try {
          // Check if font is already loaded
          const existingFont = Array.from(document.fonts).find(
            (font) => font.family.toLowerCase() === fontData.name.toLowerCase()
          );

          if (existingFont && existingFont.status === "loaded") {
            return { success: true, name: fontData.name, alreadyLoaded: true };
          }

          // Create font face using S3 CDN URL directly
          const fontFace = new FontFace(fontData.name, `url(${fontData.s3Url})`, {
            weight: "normal",
            style: "normal",
          });

          await fontFace.load();
          document.fonts.add(fontFace);

          return { success: true, name: fontData.name, alreadyLoaded: false };
        } catch (error) {
          console.error("Failed to load custom font from S3 CDN", { fontName: fontData.name, error });
          return { success: false, name: fontData.name, error };
        }
      });

      // Wait for all fonts to load (or fail)
      const results = await Promise.allSettled(fontLoadPromises);

      // Collect successfully loaded fonts
      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value.success) {
          loadedFontNames.push(result.value.name);
        }
      });

      const newlyLoaded = results.filter(
        (r) => r.status === "fulfilled" && r.value.success && !r.value.alreadyLoaded
      ).length;
      const alreadyLoaded = results.filter(
        (r) => r.status === "fulfilled" && r.value.success && r.value.alreadyLoaded
      ).length;
      const failed = results.filter((r) => r.status === "rejected" || !r.value?.success).length;

      console.log("✅ Loaded custom fonts from S3 CDN in parallel", {
        loaded: loadedFontNames.length,
        total: cdnFonts.length,
        newlyLoaded,
        alreadyLoaded,
        failed,
      });
    } catch (error) {
      console.error("Error loading CDN fonts:", error);
    }

    return loadedFontNames;
  }

  /**
   * Get font CDN URLs for export (returns S3 URLs for @font-face rules)
   */
  async getFontCDNUrlsForExport(usedFontNames: Set<string>): Promise<Map<string, FontCDNData>> {
    const fontsMap = new Map();

    try {
      const cdnFonts = await this.getLocalFontRegistry();

      for (const fontData of cdnFonts) {
        // Only include fonts that are actually used
        if (usedFontNames.has(fontData.name.toLowerCase())) {
          fontsMap.set(fontData.name.toLowerCase(), fontData);
          console.log("📦 Using CDN font for export", { fontName: fontData.name, s3Url: fontData.s3Url });
        }
      }
    } catch (error) {
      console.error("Error getting CDN font URLs for export:", error);
    }

    return fontsMap;
  }

  /**
   * Remove a custom font from local registry
   */
  async removeFontFromRegistry(fontName: string): Promise<void> {
    try {
      // First, remove from backend using the removeUserFont mutation
      try {
        const response = (await removeUserFont(fontName)) as any;
        const result = await response.json();

        if (result.errors) {
          console.error("❌ Backend error removing font:", result.errors[0]?.message);
          throw new Error(`Backend error: ${result.errors[0]?.message || JSON.stringify(result.errors)}`);
        }
      } catch (error) {
        console.error("❌ Error removing font from backend:", error);
        throw error; // Re-throw to prevent local removal if backend removal fails
      }

      // After successful backend removal, remove from local registry
      const existingFonts = await this.getLocalFontRegistry();

      // Filter out the font to remove
      const updatedFonts = existingFonts.filter((font) => font.name !== fontName);

      // Update local registry
      const user = authorization.getUser();
      const storageData = {
        userId: user?.id,
        clientId: user?.clientId,
        fonts: updatedFonts,
      };
      localStorage.setItem("s3_font_cdn_registry", JSON.stringify(storageData));
      // Update in-memory cache to avoid stale data
      this.cache = {
        userId: user?.id || "",
        clientId: user?.clientId || 0,
        fonts: updatedFonts,
      };

      // Remove from document.fonts if loaded
      const existingFont = Array.from(document.fonts).find(
        (font) => font.family.toLowerCase() === fontName.toLowerCase()
      );
      if (existingFont) {
        document.fonts.delete(existingFont);
      }

      console.log("✅ Font removed from local registry", { fontName });
    } catch (error) {
      console.error("Error removing font from registry:", error);
      throw new Error(`Failed to remove font from registry: ${error}`);
    }
  }

  /**
   * Get font CDN statistics
   */
  async getFontCDNStats(): Promise<{
    totalFonts: number;
    totalSize: number;
    fonts: Array<{ name: string; size: number; uploadedAt: number; s3Url: string }>;
  }> {
    try {
      const cdnFonts = await this.getLocalFontRegistry();

      let totalSize = 0;
      const fonts = cdnFonts.map((font) => {
        totalSize += font.fileSize;
        return {
          name: font.name,
          size: font.fileSize,
          uploadedAt: font.uploadedAt,
          s3Url: font.s3Url,
        };
      });

      return {
        totalFonts: cdnFonts.length,
        totalSize,
        fonts,
      };
    } catch (error) {
      console.error("Error getting font CDN stats:", error);
      return {
        totalFonts: 0,
        totalSize: 0,
        fonts: [],
      };
    }
  }

  /**
   * Invalidate the in-memory cache so the next read reloads from backend/localStorage.
   */
  clearCache(): void {
    this.cache = null;
  }

  /**
   * Clear all fonts from local registry
   */
  async clearFontRegistry(): Promise<void> {
    try {
      localStorage.removeItem("s3_font_cdn_registry");
      this.cache = null;

      // Clear from backend
      await this.syncFontsWithBackend([]);

      // Remove all custom fonts from document.fonts
      const customFonts = Array.from(document.fonts);
      const registryFonts = await this.getLocalFontRegistry();

      customFonts.forEach((font) => {
        if (registryFonts.some((rf) => rf.name.toLowerCase() === font.family.toLowerCase())) {
          document.fonts.delete(font);
        }
      });

      console.log("✅ Font CDN registry cleared");
    } catch (error) {
      console.error("Error clearing font registry:", error);
    }
  }
}

// Export singleton instance
export const s3CustomFontService = new S3CustomFontService();
